/**
 * Helper components and utilities for MenuSheet.
 * Extracted to keep MenuSheet.tsx under the 400-line limit (FR-013).
 */

import { type JSX, type ReactNode, Suspense } from 'react';
import { useLanguage } from '../i18n/useLanguage.js';
import { useTranslate } from '../i18n/useTranslate.js';
import { useComposeWizard } from '../member-messaging/compose/wizard/ComposeWizardProvider.js';
import { useMemberProfile } from '../member-profile/useMemberProfile.js';
import { Select } from '../primitives/Select.js';
import type { AuthState } from '../session/SessionPort.js';
import { useSession } from '../session/useSession.js';
import { useSignOut } from '../session/useSignOut.js';
import { deriveAvatarInitials } from './avatarInitials.js';
import { ChevronDownIcon, GlobeIcon, SignInIcon, SignOutIcon } from './icons.js';
import {
  menuChipAvatarClasses,
  menuChipBadgeClasses,
  menuChipBodyClasses,
  menuChipNameClasses,
  menuChipNameRowClasses,
  menuChipNumberClasses,
  menuLanguageToggleClasses,
  menuLanguageValueClasses,
  menuLanguageValueIconClasses,
  menuLinkClasses,
  menuLinkDisabledClasses,
  menuLinkIconClasses,
  menuLinkLabelClasses,
  menuSignOutClasses,
} from './menuClasses.js';
import type { MenuShortcut } from './menuServiceEntries.js';
import { PortErrorBoundary } from './PortErrorBoundary.js';

export function assertNever(value: never): never {
  throw new Error(`Unhandled discriminated-union variant: ${JSON.stringify(value)}`);
}

export function MemberNumberSkeleton(): JSX.Element {
  return (
    <span
      className={`${menuChipNumberClasses} inline-block bg-neutral-200 rounded w-24 h-5 animate-pulse`}
    />
  );
}

/**
 * The "Jäsennumero: <number>" chip row. Sources the number from the trusted
 * SESSION (`state.memberNumber`, the server-derived jäsennumero on the real
 * member arm, 032 RD-111 / T016) when present — a synchronous read, no Suspense.
 * Falls back to the member-profile port (`MemberNumberFromProfile`, which
 * suspends) only when the session carries no number: the demo adapter sets
 * `demoMemberKey`, not `memberNumber`, so the demo path still resolves through
 * the port. That fallback retires with the demo adapter (T035), after which
 * every member arm carries `memberNumber` and the port read here is dead.
 */
export function MemberNumberRow({
  className = menuChipNumberClasses,
}: {
  readonly className?: string;
}): JSX.Element {
  const { state } = useSession();
  const memberNumber = state.kind === 'member' ? state.memberNumber : undefined;
  if (memberNumber !== undefined) {
    return <MemberNumberText value={memberNumber} className={className} />;
  }
  return <MemberNumberFromProfile className={className} />;
}

/**
 * Renders the member-number row from a resolved number value. Pure (no port
 * read), so the real-session path needs no Suspense boundary.
 */
function MemberNumberText({
  value,
  className,
}: {
  readonly value: string;
  readonly className: string;
}): JSX.Element {
  const t = useTranslate();
  return (
    <span className={className}>{`${t('shell.menu.userChip.memberNumberPrefix')} ${value}`}</span>
  );
}

/**
 * Demo-path fallback: reads the member number from the member-profile port
 * (suspends while it resolves; the MenuSheet caller wraps this in `<Suspense>`).
 * Reached only when the session arm carries no `memberNumber` — i.e. under the
 * demo adapter. Retires with the demo adapter (T035).
 */
function MemberNumberFromProfile({ className }: { readonly className: string }): JSX.Element {
  const profile = useMemberProfile();
  return <MemberNumberText value={profile.contact.memberNumber} className={className} />;
}

/**
 * Demo-identity badge for the profile chip. Renders the "demo" tag ONLY for a
 * DEMO member session (`kind: 'member', demo: true`). The real admin (BFF) and a
 * real member are both `demo: false`, so neither is tagged demo (FR-010, the
 * honest-demo contract — real identities come from an IdP claim set, not the demo
 * fixtures). Before this fix the badge rendered for every signed-in user, so the
 * real admin was mislabelled "demo". Returns `null` otherwise so the chip's name
 * row shows just the display name.
 */
export function MenuChipDemoBadge({
  authState,
}: {
  readonly authState: AuthState;
}): JSX.Element | null {
  const t = useTranslate();
  // `kind === 'member'` narrows `authState` to the arm that carries `demo`; an
  // admin / real member (both `demo: false`), loading, and anonymous show nothing.
  const isDemo = authState.kind === 'member' && authState.demo;
  if (!isDemo) {
    return null;
  }
  return <span className={menuChipBadgeClasses}>{t('shell.menu.userChip.demoBadge')}</span>;
}

/**
 * The signed-in identity shown at the top of the menu sheet and the desktop
 * sidebar: display name + demo badge, then the member number for a member
 * session (admins have none, FR-010), then an sr-only hint that the surrounding
 * link opens the profile. The caller owns the link. `avatar` adds the menu
 * sheet's initials circle; the sidebar card has none. `numberFallback` is what
 * shows while the number loads: the menu sheet keeps its skeleton, the sidebar
 * shows nothing rather than a placeholder number. `hideNumberOnError` contains a
 * failed number read to the number row (the sidebar is always mounted, so a
 * failure must not take the navigation down with it). `nameClassName` and
 * `numberClassName` let the sidebar use the prototype's desktop text sizes.
 */
export function MemberChipContent({
  avatar,
  numberFallback,
  hideNumberOnError = false,
  nameClassName = menuChipNameClasses,
  numberClassName = menuChipNumberClasses,
}: {
  readonly avatar: boolean;
  readonly numberFallback: ReactNode;
  readonly hideNumberOnError?: boolean;
  readonly nameClassName?: string;
  readonly numberClassName?: string;
}): JSX.Element | null {
  const t = useTranslate();
  const { state } = useSession();
  if (state.kind !== 'member' && state.kind !== 'admin') {
    return null;
  }
  return (
    <>
      {avatar && (
        <span className={menuChipAvatarClasses} aria-hidden="true">
          {deriveAvatarInitials(state.displayName)}
        </span>
      )}
      <span className={menuChipBodyClasses}>
        {/* The demo badge shares the name's row so it reads as an identity
            descriptor, not a member-number qualifier (B-INV-017, B-INV-018). */}
        <span className={menuChipNameRowClasses}>
          <span className={nameClassName}>{state.displayName}</span>
          <MenuChipDemoBadge authState={state} />
        </span>
        {state.kind === 'member' &&
          (hideNumberOnError ? (
            <PortErrorBoundary portName="memberProfile" fallback={null}>
              <Suspense fallback={numberFallback}>
                <MemberNumberRow className={numberClassName} />
              </Suspense>
            </PortErrorBoundary>
          ) : (
            <Suspense fallback={numberFallback}>
              <MemberNumberRow className={numberClassName} />
            </Suspense>
          ))}
        <span className="sr-only">{t('shell.menu.userChip.openProfile')}</span>
      </span>
    </>
  );
}

/**
 * Sign-out for every shell surface. `/login` is the SERVER member Telia-start
 * (032 US1, proxied): a client `navigate('/login')` renders the retiring SPA
 * demo form, never Telia. Use the SAME full-page `window.location.replace`
 * RouteGuard's wall uses — deterministic, so admin sign-out no longer loses the
 * React nav race (spec 023). Await the revoke first so the POST completes
 * before the full-page nav tears the page down.
 */
export function useShellSignOut(): () => Promise<void> {
  return useSignOut();
}

/**
 * An `action`-kind service row (027 US6 / D-120): a button styled like the
 * flat-link rows that triggers an in-app action instead of navigating — today
 * the only action opens the compose wizard (the app's single "Ota yhteyttä"
 * experience, FR-023). The drawer closes first so the modal opens over the
 * page, not the menu. Entry-UNGATED for members by lead decision: the wizard
 * follows its destination's shipped posture (flow-level 028 gating). Under D-130
 * a logged-out visitor is walled to `/login` and never opens this menu in the
 * running app; the anonymous branch (component tests / the brief pre-redirect
 * frame) holds no compose identity, so the caller passes `disabled` for anon —
 * the row greys (aria-disabled, no-op) like the flat-link and expandable rows.
 * The join CTA lives in the terminal `/ei-jasenyytta` `NoMembershipBlock`
 * treatment, not here.
 */
export function MenuActionEntryRow({
  entry,
  closeMenu,
  returnFocusTo,
  disabled = false,
}: {
  readonly entry: Extract<MenuShortcut, { kind: 'action' }>;
  readonly closeMenu: () => void;
  readonly returnFocusTo?: HTMLElement | null;
  readonly disabled?: boolean;
}): JSX.Element {
  const t = useTranslate();
  const composeWizard = useComposeWizard();
  return (
    <button
      type="button"
      aria-disabled={disabled || undefined}
      className={disabled ? menuLinkDisabledClasses : menuLinkClasses}
      onClick={
        disabled
          ? undefined
          : () => {
              closeMenu();
              switch (entry.actionId) {
                case 'open-compose-wizard':
                  composeWizard.open(returnFocusTo);
                  return;
                default:
                  // Exhaustiveness guard: a new MenuActionId without a dispatch
                  // branch fails the compile here.
                  assertNever(entry.actionId);
              }
            }
      }
    >
      <entry.Icon aria-hidden="true" className={menuLinkIconClasses} />
      <span className={menuLinkLabelClasses}>{t(entry.labelKey)}</span>
    </button>
  );
}

/**
 * An `external-link` service row: a real anchor to an off-app destination
 * (026/US2, spec FR-007) — the two ytkpalvelut.fi legal pages. `target=
 * "_blank"` + `rel="noopener noreferrer"` open a new tab without granting
 * the destination `window.opener` access. `no-raw-anchor-internal` permits
 * the raw `<a>` because the href is an absolute external URL (internal
 * routes MUST use `<NavLink>`). Leading icon + label, matching the
 * prototype's legal-link glyphs (FileText / Lock). The drawer is NOT
 * closed on click: a new tab opens, the
 * menu stays as the user left it.
 */
export function MenuExternalLinkRow({
  entry,
}: {
  readonly entry: Extract<MenuShortcut, { kind: 'external-link' }>;
}): JSX.Element {
  const t = useTranslate();
  return (
    <a href={entry.href} target="_blank" rel="noopener noreferrer" className={menuLinkClasses}>
      <entry.Icon aria-hidden="true" className={menuLinkIconClasses} />
      <span className={menuLinkLabelClasses}>{t(entry.labelKey)}</span>
    </a>
  );
}

/**
 * Member language picker. The house Select exposes all supported member UI
 * locales in one keyboard-accessible control. It is always rendered (including
 * for anonymous visitors and on /login), so people can choose their language
 * before signing in.
 */
export function MenuLanguageToggle(): JSX.Element {
  const t = useTranslate();
  const { active, set } = useLanguage();

  return (
    <Select.Root
      value={active}
      onValueChange={(next) => {
        if (next === 'fi' || next === 'en' || next === 'sv') {
          void set(next);
        }
      }}
    >
      <Select.Trigger asChild aria-label={t('shell.menu.languagePicker.label')}>
        <button type="button" className={menuLanguageToggleClasses}>
          <GlobeIcon aria-hidden="true" className={menuLinkIconClasses} />
          <span className={menuLinkLabelClasses}>{t('shell.menu.languagePicker.label')}</span>
          <span className={menuLanguageValueClasses}>
            <Select.Value />
            <ChevronDownIcon aria-hidden="true" className={menuLanguageValueIconClasses} />
          </span>
        </button>
      </Select.Trigger>
      <Select.Content widthMode="fit">
        <Select.Item value="fi" inset="narrow">
          {t('shell.menu.languagePicker.fi')}
        </Select.Item>
        <Select.Item value="en" inset="narrow">
          {t('shell.menu.languagePicker.en')}
        </Select.Item>
        <Select.Item value="sv" inset="narrow">
          {t('shell.menu.languagePicker.sv')}
        </Select.Item>
      </Select.Content>
    </Select.Root>
  );
}

/**
 * Sign-out row — proto pattern: full-width destructive-tinted row + leading
 * sign-out icon. Rendered for a signed-in member/admin only.
 */
export function MenuSignOutRow({ onSignOut }: { readonly onSignOut: () => void }): JSX.Element {
  const t = useTranslate();
  return (
    <button type="button" className={menuSignOutClasses} onClick={onSignOut}>
      <SignOutIcon aria-hidden="true" className="h-5 w-5" />
      <span>{t('shell.menu.signOut')}</span>
    </button>
  );
}

/**
 * Sign-in row — for an anonymous session the sign-out row is replaced by this
 * in-app "Kirjaudu sisään" affordance (-> /login). Under D-130 the login wall is
 * restored, so anon is redirected to `/login` and this menu is not reachable in
 * the running app; the row serves the anonymous render branch (component tests /
 * the brief pre-redirect frame). Neutral row styling (a positive action, not the
 * destructive tint).
 */
export function MenuSignInRow({ onSignIn }: { readonly onSignIn: () => void }): JSX.Element {
  const t = useTranslate();
  return (
    <button type="button" className={menuLinkClasses} onClick={onSignIn}>
      <SignInIcon aria-hidden="true" className={menuLinkIconClasses} />
      <span className={menuLinkLabelClasses}>{t('shell.menu.signIn')}</span>
    </button>
  );
}
